import { useEffect, useMemo, useState } from 'react'
import { Clock, History } from 'lucide-react'
import api from '@/services/api'
import type { ClienteAgradaDetail, ClienteAgradaHistoricoItem } from '@/services/clientesService'
import { FinanceDetailSectionCard } from '@/components/Financeiro/FinanceDetailSectionCard'
import { formatDateTime } from '@/utils/format-date'
import { cn } from '@/utils/cn'
import { HISTORICO_TIPO_LABELS } from './clienteAgradaDetailUtils'

type EmpresaHistoryItem = {
  id: string
  action: string
  details?: string
  createdAt: string
  user?: { name: string }
}

type Props = {
  detail: ClienteAgradaDetail
}

const TIPO_ACCENT: Record<string, string> = {
  cadastro: '#10b981',
  cliente_ativo: '#22c55e',
  deal: '#987de8',
  projeto: '#3b82f6',
  chamado: '#f59e0b',
  atividade: '#ef4444',
}

export function ClienteAgradaHistoricoTab({ detail }: Props) {
  const [auditHistory, setAuditHistory] = useState<EmpresaHistoryItem[]>([])
  const [loadingAudit, setLoadingAudit] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    const fetchHistory = async () => {
      try {
        const resp = await api.get(`/empresas/${detail.id}/history`, { signal: controller.signal })
        setAuditHistory(resp.data.data ?? [])
      } catch {
        setAuditHistory([])
      } finally {
        setLoadingAudit(false)
      }
    }
    void fetchHistory()
    return () => controller.abort()
  }, [detail.id])

  const mergedTimeline = useMemo(() => {
    const fluxos: ClienteAgradaHistoricoItem[] = detail.historicoFluxos ?? []
    const audit: ClienteAgradaHistoricoItem[] = auditHistory.map((item) => ({
      id: item.id,
      tipo: 'atividade' as const,
      titulo: item.action,
      detalhe: [item.details, item.user?.name].filter(Boolean).join(' · ') || undefined,
      data: item.createdAt,
    }))

    const seen = new Set<string>()
    return [...fluxos, ...audit]
      .filter((item) => {
        const key = `${item.tipo}-${item.id}-${item.data}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .sort((a, b) => b.data.localeCompare(a.data))
  }, [auditHistory, detail.historicoFluxos])

  if (loadingAudit && mergedTimeline.length === 0) {
    return (
      <div className='glass-card flex justify-center py-16'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  if (mergedTimeline.length === 0) {
    return (
      <FinanceDetailSectionCard title='Histórico' icon={History} accent='#64748b'>
        <div className='py-10 text-center'>
          <Clock size={40} className='mx-auto mb-3 text-text-muted opacity-50' aria-hidden />
          <p className='font-medium text-text-primary'>Nenhum histórico encontrado</p>
          <p className='mt-1 text-sm text-text-muted'>
            Movimentações de deals, projetos e chamados aparecerão aqui.
          </p>
        </div>
      </FinanceDetailSectionCard>
    )
  }

  return (
    <FinanceDetailSectionCard
      title='Histórico de fluxos'
      icon={History}
      accent='#ef4444'
      headerExtra={
        <span className='text-xs text-text-muted'>
          {mergedTimeline.length} evento{mergedTimeline.length === 1 ? '' : 's'}
        </span>
      }
    >
      <ul className='space-y-0'>
        {mergedTimeline.map((item, index) => {
          const accent = TIPO_ACCENT[item.tipo] ?? '#64748b'
          const isLast = index === mergedTimeline.length - 1
          return (
            <li key={`${item.tipo}-${item.id}-${item.data}`} className='relative flex gap-4 pb-6'>
              {!isLast && (
                <span
                  className='absolute left-[11px] top-6 h-[calc(100%-12px)] w-px bg-white/[0.08]'
                  aria-hidden
                />
              )}
              <span
                className='relative z-[1] mt-1.5 h-[22px] w-[22px] shrink-0 rounded-full border-2'
                style={{ borderColor: accent, backgroundColor: `${accent}22` }}
              />
              <div className='min-w-0 flex-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
                <div className='flex flex-wrap items-start justify-between gap-2'>
                  <div className='min-w-0'>
                    <p className='font-semibold text-text-primary'>{item.titulo}</p>
                    {item.detalhe && (
                      <p className='mt-1 text-sm text-text-muted'>{item.detalhe}</p>
                    )}
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    )}
                    style={{ backgroundColor: `${accent}18`, color: accent }}
                  >
                    {HISTORICO_TIPO_LABELS[item.tipo] ?? item.tipo}
                  </span>
                </div>
                <p className='mt-2 text-xs text-text-muted'>{formatDateTime(item.data)}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </FinanceDetailSectionCard>
  )
}
